// #region Imports
import { FormProvider } from 'react-hook-form';
import { AZModal } from '../AZModal';
import './AZFeedback.css';
import type { AZFeedbackProps } from './AZFeedback.types';
import { BugScreen } from './BugScreen';
import { ChoiceScreen } from './ChoiceScreen';
import { FeedbackScreen } from './FeedbackScreen';
import { useAZFeedback } from './useAZFeedback';
// #endregion

// #region Component
/**
 * Monthly feedback modal driven by Redux state.
 * Shows a notification (via `onExpired`) when the last feedback date was more than 1 month ago.
 * The consumer controls the notification UI — use `onExpired` to show a toast, badge, etc.
 * Can also be opened manually via `dispatch(setFeedBackOpen('manually'))`.
 *
 * Requires `feedbackSlice.reducer` mounted at `state.feedback` in your Redux store.
 *
 * @example Basic usage with sonner toast
 * import { toast } from 'sonner';
 *
 * <AZFeedback
 *   appName="MyApp"
 *   appVersion="1.2.0"
 *   username={user.name}
 *   userEmail={user.email}
 *   onSendFeedback={async (data) => { await api.post('/feedback', data); }}
 *   onExpired={(handleOpen) =>
 *     toast('Give us your feedback! 👋', {
 *       duration: Infinity,
 *       action: { label: 'Open', onClick: handleOpen },
 *     })
 *   }
 * />
 */
const AZFeedback = (props: AZFeedbackProps) => {
  const {
    isFeedbackOpen,
    formChoice,
    isLoading,
    methods,
    setFormChoice,
    handleSendFeedback,
    handleValidateStep,
    handleGoBack,
    handleClose,
  } = useAZFeedback(props);

  const isManual = isFeedbackOpen === 'manually';
  // Only open when a screen is ready to render — avoids a transient frame where
  // the Dialog is open but no Dialog.Title exists (accessibility violation).
  const isOpen = Boolean(isFeedbackOpen) && (isManual || formChoice !== null);

  // #region Interface
  return (
    <AZModal show={isOpen} onClose={handleClose} fullscreen={false} maxWidth="560px">
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(
            handleSendFeedback as Parameters<typeof methods.handleSubmit>[0],
          )}
          noValidate
        >
          {/* Manual mode with no choice made → show choice screen */}
          {isManual && !formChoice && (
            <div className="az-feedback-screen-enter">
              <ChoiceScreen onSelect={setFormChoice} onClose={handleClose} />
            </div>
          )}

          {/* Feedback wizard (manual choice or auto-expired) */}
          {formChoice === 'feedback' && (
            <div className="az-feedback-screen-enter">
              <FeedbackScreen
                isManual={isManual}
                isLoading={isLoading}
                onValidateStep={handleValidateStep}
                onGoBack={handleGoBack}
                onClose={handleClose}
              />
            </div>
          )}

          {/* Bug report */}
          {formChoice === 'bug' && (
            <div className="az-feedback-screen-enter">
              <BugScreen
                isManual={isManual}
                isLoading={isLoading}
                attachmentOptions={props.attachmentOptions}
                onGoBack={handleGoBack}
                onClose={handleClose}
              />
            </div>
          )}
        </form>
      </FormProvider>
      <div className="az-feedback-logo" aria-hidden="true">
        AZmed
      </div>
    </AZModal>
  );
  // #endregion
};
// #endregion

// #region Display Name
AZFeedback.displayName = 'AZFeedback';
// #endregion

export { AZFeedback };
